import React, { useEffect, useState, useRef } from 'react';
import { normalizeSearch } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ListItem } from '../../types/list';
import { listClient } from '../../lib/list';
import { useHeader } from '../../context/HeaderContext';
import { SearchAndFilterBar } from '../standard/SearchAndFilterBar';
import { FilterDrawer } from '../shared/FilterDrawer';
import type { FilterState } from '../shared/FilterDrawer';
import { ListGrid } from './ListGrid';
import { DetailCard } from './DetailCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { useAnalytics } from '../../hooks/useAnalytics';
import ErrorBoundary from '../analytics/ErrorBoundary';

const ListSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);

  const analytics = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'liste'
  });

  const [selectedItem, setSelectedItem] = useState<ListItem | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterState>({ category: '', types: [], priceFilter: [] });

  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ['list-items', section?.id],
    queryFn: async () => {
      if (!section?.id) throw new Error('Section ID not found for this slug');
      return listClient.getItems(section.id);
    },
    enabled: !!section?.id,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000
  });

  useEffect(() => {
    if (section?.id) analytics.trackSectionView('list');
  }, [section?.id, analytics]);

  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();
    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();
    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });
    return colorMap;
  }, [config, items]);

  const filteredItems = React.useMemo(() => {
    return items.filter(item => {
      const categoryMatch = !filters.category || item.category === filters.category;
      const typeMatch = filters.types.length === 0 || filters.types.includes(item.type || '');
      const q = normalizeSearch(searchQuery.trim());
      const searchMatch =
        !q ||
        normalizeSearch(item.name).includes(q) ||
        normalizeSearch(item.fulladress || '').includes(q) ||
        normalizeSearch(item.category || '').includes(q) ||
        normalizeSearch(item.type || '').includes(q);
      return categoryMatch && typeMatch && searchMatch;
    });
  }, [items, filters, searchQuery]);

  const activeFiltersCount =
    (filters.category ? 1 : 0) + filters.types.length + filters.priceFilter.length;

  useEffect(() => {
    if (section?.names) {
      const n = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(n);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items) {
      const item = items.find(i => slugify(i.name) === name);
      if (item) {
        setSelectedItem(item);
        analytics.trackItemAction(item.id, 'item_click', 'liste', {
          item_name: item.name,
          category: item.category,
          type: item.type,
          source: 'url_navigation'
        });
      }
    }
  }, [name, items, analytics]);

  const handleBackClick = () => {
    analytics.trackEvent('navigation', 'menu_click', {
      target: 'home',
      section_id: section?.id,
      section_name: section?.names?.fr
    });
    navigate('/');
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    analytics.trackFilter('filter_change', JSON.stringify(newFilters));
    setFilters(newFilters);
    setSelectedItem(null);
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleItemClick = (item: ListItem) => {
    scrollPositionRef.current = window.scrollY;
    analytics.trackItemAction(item.id, 'item_click', 'liste', {
      item_name: item.name,
      category: item.category,
      type: item.type
    });
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}/${slugify(item.name)}`, { replace: true });
    setSelectedItem(item);
  };

  const handleDetailClose = () => {
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`, { replace: true });
    analytics.trackEvent('interaction', 'detail_card_close', {
      section_id: section?.id,
      item_id: selectedItem?.id,
      item_name: selectedItem?.name,
      view_duration: selectedItem ? Date.now() - scrollPositionRef.current : 0
    });
    setSelectedItem(null);
    setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 50);
  };

  if (configLoading || translationsLoading || itemsLoading) return <LoadingIndicator />;

  if (itemsError || !config || !translations || !section) {
    analytics.trackError('section_load_error', itemsError || new Error('Missing configuration'), {
      section_id: section?.id,
      section_type: 'liste'
    });
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors">
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary componentName="ListSection" sectionId={section.id} sectionName={section.names.fr}>
      <div className="min-h-screen" style={{ backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb') }}>
        <SectionHeader
          config={config}
          title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />

        <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
          <SearchAndFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onFiltersOpen={() => setIsFilterDrawerOpen(true)}
            activeFiltersCount={activeFiltersCount}
            config={config}
            translations={translations}
          />
        </div>

        <main className="main-content">
          <ListGrid
            items={filteredItems}
            selectedCategory={filters.category}
            onItemClick={handleItemClick}
            onCategorySelect={(cat) => handleFiltersChange({ ...filters, category: cat, types: [] })}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
          />
        </main>

        {selectedItem && (
          <DetailCard
            item={selectedItem}
            items={filteredItems}
            onClose={handleDetailClose}
            onItemChange={(newItem) => {
              const itemSlug = slugify(newItem.name);
              analytics.trackItemAction(newItem.id, 'navigation_next', 'liste', {
                item_name: newItem.name,
                category: newItem.category,
                type: newItem.type,
                previous_item_id: selectedItem?.id,
                previous_item_name: selectedItem?.name
              });
              const sectionSlug = slugify(section.names.fr);
              navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
              setSelectedItem(newItem);
            }}
            onCategorySelect={(cat) => handleFiltersChange({ ...filters, category: cat, types: [] })}
            onTypeSelect={(type) => handleFiltersChange({ ...filters, types: type ? [type] : [] })}
            config={config}
            translations={translations}
            tableId={section.id}
            categoryColors={categoryColors}
          />
        )}

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          items={items}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ListSection;
export { ListSection };
